import type { SupabaseClient } from '@supabase/supabase-js';
import { renderListToGambioTab } from './tabs/list-to-gambio.js';
import { infoBadge } from '../lib/info-badge.js';
import { helpButtonHtml, wireHelpButtons } from '../lib/help-panel.js';
import { connectionPillHtml } from '../lib/connection-pill.js';

// Standalone-Fenster „Auf Gambio anlegen" für den Vario-Verkaufskanal-Tab (Slot
// settings.sales-channels.{appId}.tabs → /#/list-to-gambio).
//
// Warum es diese Seite gibt: Die Massen-Anlage steckte bis 24.09.2026 ausschliesslich als
// Unterreiter in `views/dashboard.ts`. Dieser Dashboard-Slot steht aber seit laengerem nicht mehr
// im Manifest, `readSlot()` faellt nur noch darauf zurueck, wenn gar nichts passt. Damit war
// „Auf Gambio anlegen" in VARIO ueberhaupt nicht erreichbar, obwohl das Handbuch es beschreibt.
// Statt den Dashboard-Slot samt eigener Reiterleiste in die VARIO-Reiterleiste zu haengen (dann
// gaebe es Bestellungen, Produkte, Log und Anleitung doppelt), bekommt die Funktion einen eigenen
// Verkaufskanal-Tab. Der Kanal hat danach genau die fuenf Reiter, die das Handbuch nennt.
//
// Aufbau wie products-page.ts: Kopfzeile mit den beiden Logos, darunter der unveraenderte Tab.
// `renderListToGambioTab` setzt `host.innerHTML` selbst, deshalb bekommt es einen eigenen
// Container UNTER dem Kopf statt `contentEl` direkt.
//
// WICHTIG: `contentEl` braucht die Klasse `go-products`. Saemtliche Kopf-Regeln in
// `gambio-products.css` sind darauf gescoped (`.go-products .apphead`, `.brand img`,
// `.gambio-logo` …). Ohne sie greift keine einzige davon: die beiden Logos haben dann gar
// keine Hoehe mehr und laufen als Bilder auf volle Breite auf (Befund 24.09.2026 im echten
// Verkaufskanal). `renderProductsTab` setzt die Klasse selbst auf seinen Host und reicht den
// Kopf durch; `renderListToGambioTab` tut das nicht, also setzt sie diese Seite.

interface ListToGambioPageContext {
  contentEl: HTMLElement;
  supabase: SupabaseClient;
  session: { token: string; tenantId: string; marketplaceKind: string };
  supabaseUrl: string;
}

const HEADER_HTML = `
  <header class="apphead">
    <a class="brand" href="https://www.go-ecommerce.de" target="_blank" rel="noopener" title="go eCommerce">
      <img src="/logo-black.png" alt="go eCommerce" />
    </a>
    <img class="gambio-logo" src="/gambio-logo.png?v=2" alt="Gambio" />
    <span class="hdivider"></span>
    <div class="htitle">
      <div class="heyebrow">Gambio · Anlegen</div>
      <h1>Auf Gambio anlegen ${infoBadge('Mehrere Vario-Artikel auf einmal im Gambio-Shop anlegen oder aktualisieren.')}</h1>
    </div>
    <div class="hactions">${helpButtonHtml('auf-gambio-anlegen')}${connectionPillHtml()}</div>
  </header>
`;

export async function renderListToGambioPage(ctx: ListToGambioPageContext): Promise<void> {
  ctx.contentEl.hidden = false;

  // Vario gibt diesem Slot via useFullHeight (main.ts) die volle Viewport-Hoehe. Ancestor-Kette
  // auf 100%, damit das Grid INTERN scrollt statt die Seite wachsen zu lassen.
  document.documentElement.style.height = '100%';
  document.body.style.height = '100%';
  const appEl = document.getElementById('app');
  if (appEl) appEl.style.height = '100%';
  ctx.contentEl.style.height = '100%';
  ctx.contentEl.classList.add('go-products', 'go-products-fullpage');

  ctx.contentEl.innerHTML = `${HEADER_HTML}<div id="go-list-to-gambio-host" style="flex:1 1 auto; min-height:0; overflow:auto;"></div>`;
  const host = ctx.contentEl.querySelector<HTMLElement>('#go-list-to-gambio-host');
  if (!host) return;

  await renderListToGambioTab(host, {
    supabase: ctx.supabase,
    session: { token: ctx.session.token, tenantId: ctx.session.tenantId },
    supabaseUrl: ctx.supabaseUrl,
  });

  wireHelpButtons(ctx.contentEl);
}
